import { useState, useEffect, useRef } from 'react';
import {
    SearchLg,
    CheckCircle,
    XCircle,
    Package,
    ChevronUp,
    ChevronDown,
} from '@untitledui/icons';
import { Input } from '@/components/base/input/input';
import { Select } from '@/components/base/select/select';
import { Badge } from '@/components/base/badges/badges';
import { Table, TableCard } from '@/components/application/table/table';
import { getAdminContainers } from '@/data/api/admin-containers';
import type { AdminContainerRow, AdminContainerListResponse } from '@/types/api/admin-containers';
import { useTranslation } from 'react-i18next';
import { ContainerDetailDrawer } from './container-detail-drawer';
import type { BadgeColors } from '@/components/base/badges/badge-types';

const STATUS_OPTIONS_IDS = ['DRAFT', 'BOOKED', 'IN_TRANSIT', 'ARRIVED', 'ON_HOLD', 'CANCELLED', 'DELIVERED'] as const;

const STATUS_BADGE_COLORS: Record<string, BadgeColors> = {
    draft: 'gray',
    booked: 'blue',
    in_transit: 'brand',
    arrived: 'success',
    delivered: 'success',
    on_hold: 'warning',
    cancelled: 'error',
};

const PAGE_SIZE = 30;

type SortField = 'updated_at' | 'container_number' | 'status' | 'carrier_name' | 'vessel_name' | 'risk_score' | 'vip_completeness' | 'estimated_departure_date' | 'estimated_arrival_date' | 'documents_complete' | 'last_tracked_at';

export function AdminContainersPage() {
    const { t } = useTranslation(['pages:containers-admin', 'common']);
    const [data, setData] = useState<AdminContainerListResponse | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [companyFilter, setCompanyFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [page, setPage] = useState(1);
    const [sortBy, setSortBy] = useState<SortField>('updated_at');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
    const [selectedContainerId, setSelectedContainerId] = useState<string | null>(null);
    const isFirstLoad = useRef(true);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    useEffect(() => {
        setPage(1);
    }, [debouncedSearch, companyFilter, statusFilter]);

    useEffect(() => {
        const loadContainers = async () => {
            if (isFirstLoad.current) setIsLoading(true);
            try {
                const result = await getAdminContainers({
                    page,
                    page_size: PAGE_SIZE,
                    sort_by: sortBy,
                    sort_order: sortOrder,
                    search: debouncedSearch || undefined,
                    company_id: companyFilter || undefined,
                    status: statusFilter ? [statusFilter] : undefined,
                });
                setData(result);
            } catch (error) {
                console.error('Error loading containers:', error);
                setData(null);
            } finally {
                setIsLoading(false);
                isFirstLoad.current = false;
            }
        };
        loadContainers();
    }, [debouncedSearch, companyFilter, statusFilter, page, sortBy, sortOrder]);

    const refetchData = () => {
        getAdminContainers({
            page,
            page_size: PAGE_SIZE,
            sort_by: sortBy,
            sort_order: sortOrder,
            search: debouncedSearch || undefined,
            company_id: companyFilter || undefined,
            status: statusFilter ? [statusFilter] : undefined,
        }).then(setData).catch(console.error);
    };

    const toggleSort = (field: SortField) => {
        if (sortBy === field) {
            setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortBy(field);
            setSortOrder('desc');
        }
    };


    const statusOptions = [
        { id: '', label: t('filters.status') },
        ...STATUS_OPTIONS_IDS.map((s) => ({
            id: s.toLowerCase(),
            label: s.replace(/_/g, ' '),
        })),
    ];

    const companyOptions = [
        { id: '', label: t('filters.company') },
        ...(data?.companies || []).map((c) => ({
            id: c.company_id,
            label: c.company_name,
        })),
    ];

    const getStatusBadge = (status: string) => {
        const key = status.toLowerCase();
        const color = STATUS_BADGE_COLORS[key] || 'gray';
        return <Badge color={color} size="sm">{status.replace(/_/g, ' ')}</Badge>;
    };

    const getVipText = (vip: number | null) => {
        if (vip == null) return <span className="text-quaternary">-</span>;
        const pct = Math.round(vip * 100);
        const cls = pct >= 80 ? 'text-utility-success-500' : pct >= 50 ? 'text-utility-warning-500' : 'text-utility-error-500';
        return <span className={cls}>{pct}%</span>;
    };

    const getRiskText = (risk: number | null) => {
        if (risk == null) return <span className="text-quaternary">-</span>;
        const pct = Math.round(risk * 100);
        const cls = pct >= 75 ? 'text-utility-error-500' : pct >= 50 ? 'text-utility-warning-500' : 'text-utility-success-500';
        return <span className={cls}>{pct}%</span>;
    };

    const formatDate = (dateStr: string | null) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' });
    };

    const containers = data?.items ?? [];
    const totalCount = data?.total_count || 0;

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand mx-auto mb-4" />
                    <p className="text-tertiary text-sm">{t('loading')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Header */}
            <div>
                <h1 className="text-xl font-semibold text-primary">{t('title')}</h1>
                <p className="text-tertiary text-sm mt-0.5">{t('subtitle')}</p>
            </div>

            {/* Filters */}
            <div className="rounded-xl bg-primary p-4 shadow-xs ring-1 ring-secondary">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <Input
                        placeholder={t('filters.search')}
                        value={searchTerm}
                        onChange={setSearchTerm}
                        icon={SearchLg}
                        size="sm"
                    />
                    <Select
                        placeholder={t('filters.company')}
                        items={companyOptions}
                        selectedKey={companyFilter}
                        onSelectionChange={(key) => setCompanyFilter(key as string)}
                        size="sm"
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>
                    <Select
                        placeholder={t('filters.status')}
                        items={statusOptions}
                        selectedKey={statusFilter}
                        onSelectionChange={(key) => setStatusFilter(key as string)}
                        size="sm"
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>
                </div>
            </div>

            {/* Table */}
            <TableCard.Root size="sm">
                <TableCard.Header
                    title={`${t('table.title')} (${totalCount})`}
                    description={t('table.description')}
                />

                <div className="overflow-x-auto">
                    <Table>
                        <Table.Header>
                            <Table.Head isRowHeader>
                                <SortButton field="container_number" label={t('table.headers.container')} sortBy={sortBy} sortOrder={sortOrder} onToggle={toggleSort} />
                            </Table.Head>
                            <Table.Head label={t('table.headers.company')} />
                            <Table.Head label={t('table.headers.consignee')} />
                            <Table.Head label={t('table.headers.route')} />
                            <Table.Head>
                                <SortButton field="carrier_name" label={t('table.headers.carrier')} sortBy={sortBy} sortOrder={sortOrder} onToggle={toggleSort} />
                            </Table.Head>
                            <Table.Head>
                                <SortButton field="estimated_departure_date" label={t('table.headers.dates')} sortBy={sortBy} sortOrder={sortOrder} onToggle={toggleSort} />
                            </Table.Head>
                            <Table.Head>
                                <SortButton field="vip_completeness" label={t('table.headers.vip')} sortBy={sortBy} sortOrder={sortOrder} onToggle={toggleSort} />
                            </Table.Head>
                            <Table.Head>
                                <SortButton field="risk_score" label={t('table.headers.risk')} sortBy={sortBy} sortOrder={sortOrder} onToggle={toggleSort} />
                            </Table.Head>
                            <Table.Head>
                                <SortButton field="status" label={t('table.headers.status')} sortBy={sortBy} sortOrder={sortOrder} onToggle={toggleSort} />
                            </Table.Head>
                            <Table.Head>
                                <SortButton field="documents_complete" label={t('table.headers.docs')} sortBy={sortBy} sortOrder={sortOrder} onToggle={toggleSort} />
                            </Table.Head>
                            <Table.Head>
                                <SortButton field="last_tracked_at" label="Track" sortBy={sortBy} sortOrder={sortOrder} onToggle={toggleSort} />
                            </Table.Head>
                        </Table.Header>
                        <Table.Body>
                            {containers.map((row: AdminContainerRow) => (
                                <Table.Row
                                    key={row.container_id}
                                    className="cursor-pointer"
                                    onClick={() => setSelectedContainerId(row.container_id)}
                                >
                                    <Table.Cell>
                                        <div className="min-w-[100px]">
                                            <div className="font-medium text-xs text-primary">
                                                {row.container_number || row.booking_number || '-'}
                                            </div>
                                            {row.container_number && row.booking_number && (
                                                <div className="text-[10px] text-quaternary">{row.booking_number}</div>
                                            )}
                                        </div>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <span className="text-xs text-primary whitespace-nowrap">{row.company_name}</span>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <span className="text-xs text-tertiary max-w-[140px] block break-words leading-tight">{row.consignee_name || '-'}</span>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <span className="text-xs text-tertiary whitespace-nowrap">
                                            {row.origin_port_name && row.destination_port_name
                                                ? `${row.origin_port_name} \u2192 ${row.destination_port_name}`
                                                : row.origin_port_name || row.destination_port_name || '-'}
                                        </span>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <div className="min-w-[80px]">
                                            <div className="text-xs text-primary whitespace-nowrap">{row.carrier_name || '-'}</div>
                                            {row.vessel_name && (
                                                <div className="text-[10px] text-quaternary whitespace-nowrap">
                                                    {row.vessel_name}
                                                </div>
                                            )}
                                        </div>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <div className="text-xs text-tertiary whitespace-nowrap">
                                            {formatDate(row.etd)} / {formatDate(row.eta)}
                                        </div>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <span className="text-xs font-medium">{getVipText(row.vip_completeness)}</span>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <span className="text-xs font-medium">{getRiskText(row.risk_score)}</span>
                                    </Table.Cell>
                                    <Table.Cell>
                                        {getStatusBadge(row.status)}
                                    </Table.Cell>
                                    <Table.Cell>
                                        {row.documents_complete ? (
                                            <CheckCircle className="size-4 text-utility-success-500" />
                                        ) : (
                                            <XCircle className="size-4 text-utility-error-500" />
                                        )}
                                    </Table.Cell>
                                    <Table.Cell>
                                        {row.last_tracked_at ? (
                                            <div>
                                                <CheckCircle className="size-4 text-utility-success-500 inline" />
                                                <div className="text-[10px] text-quaternary mt-0.5">
                                                    {formatDate(row.last_tracked_at)}
                                                </div>
                                            </div>
                                        ) : (
                                            <XCircle className="size-4 text-utility-error-500" />
                                        )}
                                    </Table.Cell>
                                </Table.Row>
                            ))}
                        </Table.Body>
                    </Table>
                </div>

                {/* Pagination */}
                {totalCount > 0 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t border-secondary">
                        <span className="text-xs text-tertiary">
                            {((page - 1) * PAGE_SIZE) + 1}-{Math.min(page * PAGE_SIZE, totalCount)} / {totalCount}
                        </span>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                disabled={page <= 1}
                                onClick={() => setPage((p) => p - 1)}
                                className="rounded-md px-2.5 py-1.5 text-xs font-medium text-secondary ring-1 ring-secondary ring-inset hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                Prev
                            </button>
                            <button
                                type="button"
                                disabled={page * PAGE_SIZE >= totalCount}
                                onClick={() => setPage((p) => p + 1)}
                                className="rounded-md px-2.5 py-1.5 text-xs font-medium text-secondary ring-1 ring-secondary ring-inset hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </TableCard.Root>

            {containers.length === 0 && !isLoading && (
                <div className="text-center py-8">
                    <Package className="size-10 mx-auto mb-3 text-quaternary" />
                    <h3 className="text-sm font-medium text-primary mb-1">{t('table.empty.title')}</h3>
                    <p className="text-xs text-tertiary">{t('table.empty.message')}</p>
                </div>
            )}

            {selectedContainerId && (
                <ContainerDetailDrawer
                    containerId={selectedContainerId}
                    isOpen={!!selectedContainerId}
                    onClose={() => setSelectedContainerId(null)}
                    onUpdated={refetchData}
                />
            )}
        </div>
    );
}

// --- Sort button for table headers ---
function SortButton({ field, label, sortBy, sortOrder, onToggle }: {
    field: SortField;
    label: string;
    sortBy: SortField;
    sortOrder: 'asc' | 'desc';
    onToggle: (f: SortField) => void;
}) {
    return (
        <button type="button" onClick={() => onToggle(field)} className="flex items-center gap-0.5 text-xs font-semibold whitespace-nowrap text-quaternary hover:text-primary">
            {label}
            {sortBy === field && (
                sortOrder === 'asc'
                    ? <ChevronUp className="size-3" />
                    : <ChevronDown className="size-3" />
            )}
        </button>
    );
}
